/** A single language pass over the stored event and its ticket types.
 * The source stays visible while drafting; the answer is editable before one
 * atomic acceptance. Failed writes retain the operator’s corrections.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import type { Locale, LocalizedText } from "@momo/i18n";
import { html, type TemplateResult } from "lit";
import { state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { filter, firstValueFrom, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { eventsState } from "../events.state.ts";
import {
	ticketTypesEventId$,
	ticketTypesState,
} from "../ticket-types/ticket-types.state.ts";
import {
	acceptCopyAction$,
	acceptCopyResult$,
	localizeCopyAction$,
	localizeCopyResult$,
} from "./localize.state.ts";

const TARGET_LOCALES: readonly Locale[] = ["de", "en"];

const LOCALE_LABEL_KEY = {
	de: "admin.events.localize.localeDe",
	en: "admin.events.localize.localeEn",
} as const;

/** Every way a draft can fail, as the sentence the operator reads — the
 *  callable's own tokens are written for a log, never for a person. */
const FAILURE_KEY = {
	"rate-limited": "admin.events.localize.rateLimited",
	unavailable: "admin.events.localize.unavailable",
	error: "admin.events.localize.draftFailed",
} as const;

/** One locale's editable text for one subject. `name` is empty on the event
 *  subject, which has no localizable name — the event's own name is a proper
 *  noun on posters, passes and invoices, and there is no field to store one. */
type LocalePane = {
	readonly locale: Locale;
	readonly name: string;
	readonly description: string;
};

/**
 * One subject as the panel holds it: the stored strings it was drafted from,
 * kept so every pane can be read against its own source, plus the panes
 * themselves — empty until the draft lands, which is also what gives the
 * skeleton its shape.
 */
type SubjectDraft =
	| {
			readonly kind: "event";
			readonly key: string;
			readonly sourceDescription: string;
			readonly panes: readonly LocalePane[];
	  }
	| {
			readonly kind: "ticketType";
			readonly key: string;
			readonly sourceName: string;
			readonly sourceDescription: string | null;
			readonly panes: readonly LocalePane[];
	  };

/** What the operator is looking at: the strings that will be sent, the wait,
 *  or the panes they correct and accept. */
type Phase = "scope" | "drafting" | "review";

@schmancyElement("localize-copy")
export class LocalizeCopyPanel extends LocalizeMixin(SchmancyElement) {
	@stateDecorator() private phase: Phase = "scope";
	@stateDecorator() private drafts: readonly SubjectDraft[] = [];
	/** The language the callable read the submission as — what tells the
	 *  operator which pane is their own words restated and which is a
	 *  translation. Lives only as long as this review does. */
	@stateDecorator() private detectedLocale: Locale | null = null;
	@stateDecorator() private saving = false;
	@stateDecorator() private draftFailure: keyof typeof FAILURE_KEY | null =
		null;
	@stateDecorator() private saveFailed = false;
	private eventId = "";

	override connectedCallback(): void {
		super.connectedCallback();
		// The subject is whichever event the ticket-type listener is currently
		// scoped to — the same id the form that opened this panel pushed — so the
		// panel mounts bare, with no property to keep in step.
		this.eventId = ticketTypesEventId$.value ?? "";
		const event = eventsState.value.find((e) => e.id === this.eventId);
		const description = event?.description?.trim() ?? "";
		this.drafts = [
			...(event && description
				? [
						{
							kind: "event" as const,
							key: event.id,
							sourceDescription: description,
							panes: [],
						},
					]
				: []),
			...ticketTypesState.value.map((type) => ({
				kind: "ticketType" as const,
				key: type.id,
				sourceName: type.name,
				sourceDescription: type.description?.trim() || null,
				panes: [],
			})),
		];
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private async draft(): Promise<void> {
		if (this.phase === "drafting" || this.drafts.length === 0) return;
		this.phase = "drafting";
		try {
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				localizeCopyResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
				// Closing the panel mid-run is an ordinary path — the operator changed
				// their mind, the call is already paid for, and there is nobody left to
				// tell. takeUntil completing an unfulfilled firstValueFrom rejects.
				{ defaultValue: null },
			);
			localizeCopyAction$.next({
				requestId,
				subjects: this.drafts.map((subject) =>
					subject.kind === "event"
						? {
								kind: "event" as const,
								key: subject.key,
								description: subject.sourceDescription,
							}
						: {
								kind: "ticketType" as const,
								key: subject.key,
								name: subject.sourceName,
								description: subject.sourceDescription,
							},
				),
				targetLocales: [...TARGET_LOCALES],
			});
			const result = await resultPromise;
			if (!result) return;
			if (!result.ok) {
				this.phase = "scope";
				this.draftFailure = result.reason;
				return;
			}
			this.detectedLocale = result.output.detectedLocale;
			this.drafts = this.drafts.map((subject) => {
				const answer = result.output.subjects.find(
					(s) => s.key === subject.key,
				);
				return {
					...subject,
					// A locale the answer skipped leaves an empty pane rather than no
					// pane: the operator can still type it themselves, and an empty one
					// simply does not reach the stored map.
					panes: TARGET_LOCALES.map((locale) => ({
						locale,
						name:
							answer?.kind === "ticketType"
								? (answer.copies.find((c) => c.locale === locale)?.name ?? "")
								: "",
						description:
							answer?.copies.find((c) => c.locale === locale)?.description ??
							"",
					})),
				};
			});
			this.phase = "review";
		} catch {
			this.draftFailure = "error";
			this.phase = "scope";
		} finally {
			if (this.phase === "drafting") this.phase = "scope";
		}
	}

	private editPane(
		key: string,
		locale: Locale,
		field: "name" | "description",
		value: string,
	): void {
		this.drafts = this.drafts.map((subject) =>
			subject.key === key
				? {
						...subject,
						panes: subject.panes.map((pane) =>
							pane.locale === locale ? { ...pane, [field]: value } : pane,
						),
					}
				: subject,
		);
	}

	/** The panes' own text as a stored map. A blank pane is a locale the map
	 *  simply does not carry — never an empty string, which every stored value's
	 *  own `.min(1)` refuses. */
	private static mapOf(
		panes: readonly LocalePane[],
		field: "name" | "description",
	): LocalizedText {
		const map: LocalizedText = {};
		for (const pane of panes) {
			const text = pane[field].trim();
			if (text) map[pane.locale] = text;
		}
		return map;
	}

	private async accept(): Promise<void> {
		if (this.saving || this.detectedLocale === null) return;
		const eventDraft = this.drafts.find((d) => d.kind === "event");
		this.saving = true;
		try {
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				acceptCopyResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
				{ defaultValue: null },
			);
			acceptCopyAction$.next({
				requestId,
				eventId: this.eventId,
				event: eventDraft
					? {
							descriptionByLocale: LocalizeCopyPanel.mapOf(
								eventDraft.panes,
								"description",
							),
						}
					: null,
				ticketTypes: this.drafts
					.filter((d) => d.kind === "ticketType")
					.map((subject) => ({
						id: subject.key,
						nameByLocale: LocalizeCopyPanel.mapOf(subject.panes, "name"),
						// Absent copy stays absent: a type the operator never wrote a
						// description for gains no localized one to fall back from.
						descriptionByLocale:
							subject.sourceDescription === null
								? null
								: LocalizeCopyPanel.mapOf(subject.panes, "description"),
					})),
			});
			const result = await resultPromise;
			this.saving = false;
			if (!result) return;
			if (!result.ok) {
				this.saveFailed = true;
				return;
			}
			this.close();
		} catch {
			this.saveFailed = true;
		} finally {
			this.saving = false;
		}
	}

	override render(): TemplateResult {
		const arms = {
			scope: () => html`
				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
					${this.t("admin.events.localize.intro")}
				</schmancy-typography>
				${when(
					this.drafts.length === 0,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t("admin.events.localize.nothingToTranslate")}
						</schmancy-typography>
					`,
					() => html`
						<schmancy-list surface="transparent">
							${repeat(
								this.drafts,
								(subject) => subject.key,
								(subject) => html`
									<schmancy-list-item readonly rounded>
										<div class="min-w-0 flex-1">
											<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
												${this.t(
													subject.kind === "event"
														? "admin.events.localize.subjectDescription"
														: "admin.events.localize.subjectTicketType",
												)}
											</schmancy-typography>
											<schmancy-typography type="body" token="sm">
												${
													subject.kind === "event"
														? subject.sourceDescription
														: subject.sourceDescription
															? `${subject.sourceName} — ${subject.sourceDescription}`
															: subject.sourceName
												}
											</schmancy-typography>
										</div>
									</schmancy-list-item>
								`,
							)}
						</schmancy-list>
					`,
				)}
			`,
			review: () => html`
				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
					${this.t("admin.events.localize.reviewIntro")}
				</schmancy-typography>
				${repeat(
					this.drafts,
					(subject) => subject.key,
					(subject) => html`
						<div class="flex flex-col gap-3">
							<schmancy-typography type="title" token="sm">
								${
									subject.kind === "event"
										? this.t("admin.events.localize.subjectDescription")
										: subject.sourceName
								}
							</schmancy-typography>
							<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
								${repeat(
									subject.panes,
									(pane) => pane.locale,
									(pane) => html`
										<schmancy-surface type="containerLow" class="flex flex-col gap-3 rounded-2xl p-4">
											<div class="flex flex-col gap-0.5">
												<schmancy-typography type="label" token="md">
													${this.t(LOCALE_LABEL_KEY[pane.locale])}
												</schmancy-typography>
												<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
													${this.t(
														pane.locale === this.detectedLocale
															? "admin.events.localize.provenanceYours"
															: "admin.events.localize.provenanceTranslated",
													)}
												</schmancy-typography>
											</div>
											${when(
												subject.kind === "ticketType",
												() => html`
													<schmancy-input
														label=${this.t("admin.events.localize.nameLabel")}
														.value=${pane.name}
														@change=${(e: CustomEvent<{ value: string }>) =>
															this.editPane(
																subject.key,
																pane.locale,
																"name",
																e.detail.value,
															)}
													></schmancy-input>
												`,
											)}
											${when(
												subject.sourceDescription !== null,
												() => html`
													<schmancy-textarea
														label=${this.t("admin.events.localize.descriptionLabel")}
														rows="4"
														resize="none"
														.autoHeight=${false}
														.value=${pane.description}
														@change=${(e: CustomEvent<{ value: string }>) =>
															this.editPane(
																subject.key,
																pane.locale,
																"description",
																e.detail.value,
															)}
													></schmancy-textarea>
												`,
											)}
										</schmancy-surface>
									`,
								)}
							</div>
						</div>
					`,
				)}
			`,
		} satisfies Record<Exclude<Phase, "drafting">, () => TemplateResult>;

		return html`
			<!-- Footer outside the scroll container (the ticket-type-form pattern)
			     so it never overlays the panes nor strands dead space beneath. -->
			<div class="flex flex-col min-w-[min(94vw,44rem)] max-h-[85vh]">
				<div ?inert=${this.saving} ${overflowWithin()} class="flex flex-col gap-4 p-4 md:p-6">
					<schmancy-typography type="title" token="md">
						${this.t("admin.events.localize.title")}
					</schmancy-typography>
					${arms[this.phase === "drafting" ? "scope" : this.phase]()}
				</div>

				<schmancy-surface opacity="translucent" class="flex flex-wrap items-center gap-3 px-4 pb-4 pt-2">
					<div class="ml-auto flex flex-wrap justify-end gap-2">
						<schmancy-button variant="outlined" ?disabled=${this.saving} @click=${() => this.close()}>
							${this.t(
								this.phase === "review"
									? "admin.events.localize.discardButton"
									: "admin.common.cancel",
							)}
						</schmancy-button>
						${when(
							this.phase === "review",
							() => html`
								<schmancy-button variant="filled" .busy=${this.saving} color=${this.saveFailed ? "error" : "primary"} @click=${() => this.accept()}>
									<span style="display:grid"><span style=${this.saveFailed ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.saveFailed ? "true" : "false"}>${this.t("admin.events.localize.acceptButton")}</span><span style=${this.saveFailed ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.saveFailed ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
								</schmancy-button>
							`,
							() => html`
								<schmancy-button
									variant="filled"
									.busy=${this.phase === "drafting"}
									color=${this.draftFailure ? "error" : "primary"}
									title=${this.draftFailure ? this.t(FAILURE_KEY[this.draftFailure]) : ""}
									?disabled=${this.drafts.length === 0}
									@click=${() => this.draft()}
								>
									<schmancy-icon slot="prefix">translate</schmancy-icon>
									<span style="display:grid"><span style=${this.draftFailure ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.draftFailure ? "true" : "false"}>${this.t("admin.events.localize.draftButton")}</span><span style=${this.draftFailure ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.draftFailure ? "false" : "true"}>${this.t("admin.common.retry")}</span></span>
								</schmancy-button>
							`,
						)}
					</div>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"localize-copy": LocalizeCopyPanel;
	}
}
